import React, { useState } from 'react';
import { FileText, Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Support = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/contact`, formData);
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-red-700 via-orange-600 to-orange-500 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3" data-testid="support-title">Support</h1>
          <p className="text-base md:text-lg text-white/90">We're here to help. Reach out anytime.</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-10">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-testid="contact-info-heading">Get In Touch</h2>
              <p className="text-gray-600 leading-relaxed">
                Have a question about a guide, need help with your purchase, or want to discuss a bulk order? We'd love to hear from you.
              </p>
            </div>

            <div className="space-y-5">
              <div className="flex items-start gap-4" data-testid="contact-email">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg shrink-0">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Email</h3>
                  <a href="mailto:support@paperworkdepot.com" className="text-orange-600 hover:text-red-700 font-medium transition-colors">
                    support@paperworkdepot.com
                  </a>
                  <p className="text-sm text-gray-500 mt-1">We typically respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-phone">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Phone</h3>
                  <a href="tel:+18382653071" className="text-orange-600 hover:text-red-700 font-medium transition-colors">
                    838-265-3071
                  </a>
                  <p className="text-sm text-gray-500 mt-1">Mon-Fri, 9am - 5pm EST</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-2">Common Questions</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span className="text-orange-500 font-bold mt-0.5">Q:</span> How do I download my guide after purchase?</li>
                <li className="text-gray-500 ml-5 mb-2">You'll receive an instant download link on the success page and via email.</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 font-bold mt-0.5">Q:</span> Can I get a refund?</li>
                <li className="text-gray-500 ml-5 mb-2">Digital products are non-refundable, but contact us if you're having issues.</li>
                <li className="flex items-start gap-2"><span className="text-orange-500 font-bold mt-0.5">Q:</span> Do you offer bulk pricing?</li>
                <li className="text-gray-500 ml-5">Yes! Email us for custom bundle deals for organizations.</li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            {submitted ? (
              <div className="bg-white rounded-xl shadow-lg border border-green-200 p-8 text-center" data-testid="contact-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                <p className="text-gray-600 mb-6">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                <Button
                  variant="outline"
                  onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', message: '' }); }}
                  data-testid="send-another-btn"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 md:p-8 space-y-5" data-testid="contact-form">
                <h2 className="text-xl font-bold text-gray-900">Send Us a Message</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="contact-name">Your Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all"
                    placeholder="John Doe"
                    required
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="contact-email">Email Address</label>
                  <input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all"
                    placeholder="john@example.com"
                    required
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="contact-message">Message</label>
                  <textarea
                    id="contact-message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all resize-none"
                    placeholder="How can we help you?"
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 text-lg font-semibold rounded-lg hover:from-orange-600 hover:to-red-700 transition-all shadow-lg"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                >
                  {sending ? (
                    <span className="flex items-center justify-center gap-2">Sending...</span>
                  ) : (
                    <span className="flex items-center justify-center gap-2"><Send size={18} /> Send Message</span>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Support;
